import { Effect } from 'effect';
import {
  BadgeCheck,
  Bandage,
  Braces,
  ClipboardList,
  Combine,
  Crop,
  EyeOff,
  FileMinus,
  FileArchive,
  FileDown,
  FileImage,
  FileOutput,
  FileScan,
  FileSpreadsheet,
  FileText,
  FileType,
  GitCompare,
  Hash,
  ImageDown,
  ImagePlus,
  Images,
  Layers,
  LayoutGrid,
  ListOrdered,
  MessageSquareOff,
  RotateCw,
  ScanText,
  Scissors,
  Settings2,
  Sheet,
  ShieldOff,
  Signature,
  Stamp,
  Table,
  TextCursorInput,
  Type,
} from 'lucide-react';
import { defineStudioTool, type StudioTool, type StudioToolCategory } from './tool';

/**
 * The Studio tool registry. Metadata only — every panel is lazy-loaded via
 * `load()` so heavy deps (pdf-lib, pdfjs) code-split per tool. NEVER import
 * tool panels or PDF libraries statically here.
 */
const TOOLS: StudioTool[] = [
  // ---- Organize ----
  defineStudioTool({
    id: 'merge',
    title: 'Merge',
    description: 'Combine PDFs and images into a single PDF, in the order you want.',
    icon: Combine,
    category: 'organize',
    keywords: ['combine', 'join', 'concatenate'],
    requiresDocument: false,
    load: () => import('../tools/merge/Panel'),
  }),
  defineStudioTool({
    id: 'split',
    title: 'Split / Extract',
    description: 'Split by page ranges or extract pages into a new PDF.',
    icon: Scissors,
    category: 'organize',
    keywords: ['split', 'extract', 'pages', 'range'],
    requiresDocument: true,
    load: () => import('../tools/split/Panel'),
  }),
  defineStudioTool({
    id: 'rotate',
    title: 'Rotate',
    description: 'Rotate all or selected pages by 90, 180, or 270 degrees.',
    icon: RotateCw,
    category: 'organize',
    keywords: ['rotate', 'orientation', 'landscape', 'portrait'],
    requiresDocument: true,
    load: () => import('../tools/rotate/Panel'),
  }),
  defineStudioTool({
    id: 'organize',
    title: 'Organize pages',
    description: 'Reorder, delete, and duplicate pages visually.',
    icon: LayoutGrid,
    category: 'organize',
    keywords: ['reorder', 'delete', 'pages', 'arrange', 'duplicate'],
    requiresDocument: true,
    load: () => import('../tools/organize/Panel'),
  }),

  defineStudioTool({
    id: 'remove-blank-pages',
    title: 'Remove blank pages',
    description: 'Detect blank pages and remove them after a quick review.',
    icon: FileMinus,
    category: 'organize',
    keywords: ['blank', 'empty', 'pages', 'remove', 'delete', 'clean'],
    requiresDocument: true,
    load: () => import('../tools/remove-blank-pages/Panel'),
  }),

  // ---- Edit ----
  defineStudioTool({
    id: 'edit-text',
    title: 'Edit text',
    description:
      'Change or remove the text already in the PDF — click a block on the page, edit it, and apply. Runs in your browser.',
    icon: TextCursorInput,
    category: 'edit',
    keywords: ['edit', 'text', 'change', 'replace', 'retype', 'typo', 'correct', 'canvas', 'layers'],
    requiresDocument: true,
    load: () => import('../tools/edit-text/Panel'),
  }),
  defineStudioTool({
    id: 'sign',
    title: 'Sign',
    description: 'Draw or upload a signature and stamp it anywhere on a page.',
    icon: Signature,
    category: 'edit',
    keywords: ['signature', 'sign', 'esign', 'stamp', 'initial'],
    requiresDocument: true,
    load: () => import('../tools/sign/Panel'),
  }),
  defineStudioTool({
    id: 'bates',
    title: 'Bates numbering',
    description:
      'Stamp sequential Bates numbers (prefix, padded counter, optional suffix and date) on every page.',
    icon: Hash,
    category: 'edit',
    keywords: ['bates', 'numbering', 'legal', 'stamp', 'exhibit', 'litigation'],
    requiresDocument: true,
    load: () => import('../tools/bates/Panel'),
  }),
  defineStudioTool({
    id: 'crop',
    title: 'Crop',
    description: 'Trim page margins visually or with precise percentages.',
    icon: Crop,
    category: 'edit',
    keywords: ['crop', 'margins', 'trim', 'resize'],
    requiresDocument: true,
    load: () => import('../tools/crop/Panel'),
  }),
  defineStudioTool({
    id: 'compress',
    title: 'Compress',
    description: 'Shrink PDF file size with Ghostscript — downsamples images, keeps text.',
    icon: FileArchive,
    category: 'edit',
    keywords: ['compress', 'reduce', 'size', 'optimize', 'shrink'],
    requiresDocument: true,
    load: () => import('../tools/compress/Panel'),
  }),
  defineStudioTool({
    id: 'compare',
    title: 'Compare',
    description: 'Compare two PDFs side by side with pixel-level diff highlighting.',
    icon: GitCompare,
    category: 'edit',
    keywords: ['compare', 'diff', 'side by side', 'changes', 'revisions'],
    requiresDocument: false,
    load: () => import('../tools/compare/Panel'),
  }),
  defineStudioTool({
    id: 'watermark',
    title: 'Watermark',
    description: 'Stamp text across every page, with position, opacity, and rotation.',
    icon: Stamp,
    category: 'edit',
    keywords: ['watermark', 'stamp', 'confidential', 'draft'],
    requiresDocument: true,
    load: () => import('../tools/watermark/Panel'),
  }),
  defineStudioTool({
    id: 'page-numbers',
    title: 'Page numbers',
    description: 'Add page numbers with position, format, and starting number.',
    icon: ListOrdered,
    category: 'edit',
    keywords: ['page', 'numbers', 'pagination', 'folio'],
    requiresDocument: true,
    load: () => import('../tools/page-numbers/Panel'),
  }),
  defineStudioTool({
    id: 'metadata',
    title: 'Metadata',
    description: 'View and edit title, author, subject, keywords, and more.',
    icon: Settings2,
    category: 'edit',
    keywords: ['metadata', 'properties', 'title', 'author'],
    requiresDocument: true,
    load: () => import('../tools/metadata/Panel'),
  }),
  defineStudioTool({
    id: 'flatten',
    title: 'Flatten',
    description: 'Flatten form fields and annotations into static page content.',
    icon: Layers,
    category: 'edit',
    keywords: ['flatten', 'forms', 'annotations', 'lock'],
    requiresDocument: true,
    load: () => import('../tools/flatten/Panel'),
  }),
  defineStudioTool({
    id: 'remove-pdf-annotations',
    title: 'Remove annotations',
    description: 'Delete comments, highlights, links, and other annotations — all or by type.',
    icon: MessageSquareOff,
    category: 'edit',
    keywords: ['annotations', 'comments', 'highlights', 'links', 'remove', 'markup'],
    requiresDocument: true,
    load: () => import('../tools/remove-pdf-annotations/Panel'),
  }),
  defineStudioTool({
    id: 'repair-pdf',
    title: 'Repair PDF',
    description: 'Rebuild damaged or unopenable PDFs with a Ghostscript rewrite.',
    icon: Bandage,
    category: 'edit',
    keywords: ['repair', 'fix', 'corrupt', 'damaged', 'broken', 'recover'],
    requiresDocument: false,
    load: () => import('../tools/repair-pdf/Panel'),
  }),

  defineStudioTool({
    id: 'add-text-to-pdf',
    title: 'Add text',
    description: 'Place new text anywhere on a page — click to position, pick size and color.',
    icon: Type,
    category: 'edit',
    keywords: ['text', 'add', 'type', 'label', 'write', 'overlay'],
    requiresDocument: true,
    load: () => import('../tools/add-text-to-pdf/Panel'),
  }),
  defineStudioTool({
    id: 'add-image-to-pdf',
    title: 'Add image',
    description: 'Stamp a PNG or JPG anywhere on a page — click to position, drag to move.',
    icon: ImagePlus,
    category: 'edit',
    keywords: ['image', 'picture', 'logo', 'stamp', 'insert', 'overlay'],
    requiresDocument: true,
    load: () => import('../tools/add-image-to-pdf/Panel'),
  }),

  defineStudioTool({
    id: 'fill-pdf-form',
    title: 'Fill form',
    description: 'Fill existing AcroForm fields (text, checkboxes, dropdowns) in a simple list.',
    icon: ClipboardList,
    category: 'edit',
    keywords: ['form', 'fill', 'fields', 'acroform', 'checkbox', 'dropdown'],
    requiresDocument: true,
    load: () => import('../tools/fill-pdf-form/Panel'),
  }),

  // ---- Security ----
  defineStudioTool({
    id: 'redact',
    title: 'Redact',
    description: 'Draw boxes over sensitive content and burn them in permanently.',
    icon: EyeOff,
    category: 'security',
    keywords: ['redact', 'blackout', 'censor', 'pii', 'sensitive'],
    requiresDocument: true,
    load: () => import('../tools/redact/Panel'),
  }),

  // ---- Convert ----
  defineStudioTool({
    id: 'ocr',
    title: 'OCR — searchable PDF',
    description:
      'Recognize text in scanned pages and rebuild the PDF with an invisible searchable text layer.',
    icon: FileScan,
    category: 'convert',
    keywords: ['ocr', 'scan', 'searchable', 'tesseract', 'recognize', 'text layer'],
    requiresDocument: true,
    load: () => import('../tools/ocr/Panel'),
  }),
  defineStudioTool({
    id: 'word-to-pdf',
    title: 'Word to PDF',
    description: 'Convert a .docx Word document to a clean, selectable-text PDF.',
    icon: FileType,
    category: 'convert',
    keywords: ['word', 'docx', 'convert', 'office'],
    requiresDocument: false,
    load: () => import('../tools/word-to-pdf/Panel'),
  }),
  defineStudioTool({
    id: 'excel-to-pdf',
    title: 'Excel to PDF',
    description: 'Turn a spreadsheet (.xlsx, .xls, .csv) into a PDF table.',
    icon: FileSpreadsheet,
    category: 'convert',
    keywords: ['excel', 'xlsx', 'xls', 'csv', 'spreadsheet', 'table'],
    requiresDocument: false,
    load: () => import('../tools/excel-to-pdf/Panel'),
  }),
  defineStudioTool({
    id: 'pdf-to-word',
    title: 'PDF to Word',
    description: 'Convert the open PDF into an editable .docx (text layer only).',
    icon: FileOutput,
    category: 'convert',
    keywords: ['word', 'docx', 'convert', 'export', 'office'],
    requiresDocument: true,
    load: () => import('../tools/pdf-to-word/Panel'),
  }),
  defineStudioTool({
    id: 'pdf-to-excel',
    title: 'PDF to Excel',
    description: 'Extract text lines from each page into an .xlsx workbook, one sheet per page.',
    icon: Table,
    category: 'convert',
    keywords: ['excel', 'xlsx', 'spreadsheet', 'table', 'sheet'],
    requiresDocument: true,
    load: () => import('../tools/pdf-to-excel/Panel'),
  }),
  defineStudioTool({
    id: 'pdf-to-csv',
    title: 'PDF to CSV',
    description: 'Extract text lines from each page into CSV files, one file per page.',
    icon: Sheet,
    category: 'convert',
    keywords: ['csv', 'spreadsheet', 'table', 'excel', 'export'],
    requiresDocument: true,
    load: () => import('../tools/pdf-to-csv/Panel'),
  }),
  defineStudioTool({
    id: 'image-to-pdf',
    title: 'Image to PDF',
    description: 'Turn JPG, PNG, WebP, or GIF images into a PDF.',
    icon: Images,
    category: 'convert',
    keywords: ['jpg', 'jpeg', 'png', 'webp', 'gif', 'image', 'photo'],
    requiresDocument: false,
    load: () => import('../tools/image-to-pdf/Panel'),
  }),
  defineStudioTool({
    id: 'pdf-to-image',
    title: 'PDF to image',
    description: 'Render pages to PNG or JPG images, downloaded as a zip.',
    icon: FileImage,
    category: 'convert',
    keywords: ['jpg', 'png', 'image', 'render', 'export'],
    requiresDocument: true,
    load: () => import('../tools/pdf-to-image/Panel'),
  }),
  defineStudioTool({
    id: 'extract-images-from-pdf',
    title: 'Extract images',
    description: 'Pull the original embedded images out of the PDF as a zip of PNGs.',
    icon: ImageDown,
    category: 'convert',
    keywords: ['images', 'extract', 'photos', 'embedded', 'export', 'zip'],
    requiresDocument: true,
    load: () => import('../tools/extract-images-from-pdf/Panel'),
  }),
  defineStudioTool({
    id: 'extract-text',
    title: 'Extract text',
    description: 'Pull the text layer out of the PDF into a .txt file.',
    icon: FileText,
    category: 'convert',
    keywords: ['text', 'extract', 'txt', 'copy'],
    requiresDocument: true,
    load: () => import('../tools/extract-text/Panel'),
  }),

  // ---- AI (Gemini, compute worker) ----
  defineStudioTool({
    id: 'ai-parse',
    title: 'AI Parse',
    description: 'Parse the document into a cited block graph with Gemini (BYOK).',
    icon: ScanText,
    category: 'ai',
    keywords: ['parse', 'ocr', 'gemini', 'layout', 'blocks'],
    requiresCompute: true,
    requiresDocument: false,
    load: () => import('../tools/ai-parse/Panel'),
  }),
  defineStudioTool({
    id: 'ai-extract',
    title: 'Quick extract',
    description: 'Extract labeled fields and table candidates from the parsed graph.',
    icon: Braces,
    category: 'analyze',
    keywords: ['extract', 'fields', 'tables', 'structured'],
    requiresCompute: true,
    requiresDocument: false,
    load: () => import('../tools/ai-extract/Panel'),
  }),
  defineStudioTool({
    id: 'ai-verify',
    title: 'Cross-check',
    description: 'Check a claim against the document with cited evidence.',
    icon: BadgeCheck,
    category: 'analyze',
    keywords: ['verify', 'claim', 'fact', 'check', 'evidence'],
    requiresCompute: true,
    requiresDocument: false,
    load: () => import('../tools/ai-verify/Panel'),
  }),
  defineStudioTool({
    id: 'ai-redact',
    title: 'PII pattern scan',
    description: 'Suggest PII redaction boxes (emails, SSNs, phones, custom terms).',
    icon: ShieldOff,
    category: 'analyze',
    keywords: ['pii', 'redact', 'ssn', 'email', 'privacy'],
    requiresCompute: true,
    requiresDocument: false,
    load: () => import('../tools/ai-redact/Panel'),
  }),
  defineStudioTool({
    id: 'ai-export',
    title: 'Export graph',
    description: 'Download the parsed graph as JSON or Markdown.',
    icon: FileDown,
    category: 'analyze',
    keywords: ['export', 'json', 'markdown', 'graph'],
    requiresCompute: true,
    requiresDocument: false,
    load: () => import('../tools/ai-export/Panel'),
  }),
];

export function listTools(): StudioTool[] {
  return Effect.runSync(listToolsEffect());
}

export function listToolsEffect(): Effect.Effect<StudioTool[]> {
  return Effect.succeed(TOOLS);
}

/**
 * Host extras merged over the shared registry without mutating it; an extra
 * with the same id as a built-in shadows it (host override).
 */
export function mergeExtraTools(extraTools: StudioTool[]): StudioTool[] {
  return Effect.runSync(mergeExtraToolsEffect(extraTools));
}

export function mergeExtraToolsEffect(extraTools: StudioTool[]): Effect.Effect<StudioTool[]> {
  return Effect.sync(() => [
    ...extraTools,
    ...TOOLS.filter((tool) => !extraTools.some((extra) => extra.id === tool.id)),
  ]);
}

export function getTool(id: string | null | undefined): StudioTool | null {
  return Effect.runSync(getToolEffect(id));
}

export function getToolEffect(id: string | null | undefined): Effect.Effect<StudioTool | null> {
  return Effect.sync(() => (id ? (TOOLS.find((tool) => tool.id === id) ?? null) : null));
}

export function toolsByCategory(): Array<{ category: StudioToolCategory; tools: StudioTool[] }> {
  return Effect.runSync(toolsByCategoryEffect());
}

export function toolsByCategoryEffect(): Effect.Effect<
  Array<{ category: StudioToolCategory; tools: StudioTool[] }>
> {
  return Effect.sync(() => {
    const order: StudioToolCategory[] = [
      'organize',
      'edit',
      'convert',
      'security',
      'ai',
      'analyze',
    ];
    return order
      .map((category) => ({ category, tools: TOOLS.filter((tool) => tool.category === category) }))
      .filter((group) => group.tools.length > 0);
  });
}
